import { useState, useCallback, useEffect } from 'react';
import { chatAPI, ChatStreamError } from '../services/chatApi';
import { isSessionExpired, touchActivity } from '../utils/sessionTimeout';

export interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  provider?: string;
  truncated?: boolean;
  isError?: boolean;
}

const WELCOME_MESSAGE: Message = {
  id: 'welcome',
  role: 'assistant',
  content: "Greetings. I'm TARS, Parth's portfolio assistant. Feel free to ask about projects, experience, or skills - I promise not to blow you out the airlock.",
  timestamp: new Date(),
};

const MESSAGES_KEY = 'tars_messages';

export const useChat = (sessionId: string | null) => {
  const [messages, setMessages] = useState<Message[]>([WELCOME_MESSAGE]);
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);

  // Load messages from localStorage on mount, unless the session has gone
  // stale — the backend has already forgotten this conversation by then.
  useEffect(() => {
    if (isSessionExpired()) {
      localStorage.removeItem(MESSAGES_KEY);
      return;
    }

    const savedMessages = localStorage.getItem(MESSAGES_KEY);
    if (savedMessages) {
      try {
        const parsed = JSON.parse(savedMessages);
        // Convert timestamp strings back to Date objects
        const messagesWithDates = parsed.map((msg: Message) => ({
          ...msg,
          timestamp: new Date(msg.timestamp),
        }));
        setMessages(messagesWithDates);
      } catch (e) {
        console.error('Failed to load messages:', e);
      }
    }
  }, []);

  // Save messages to localStorage whenever they change
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem(MESSAGES_KEY, JSON.stringify(messages));
    }
  }, [messages]);

  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || !sessionId) return;

      touchActivity();

      // Add user message
      const userMessage: Message = {
        id: `msg-${Date.now()}`,
        role: 'user',
        content: content.trim(),
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);
      setIsTyping(true);
      setError(null);

      const assistantId = `msg-${Date.now()}-response`;
      let assistantAdded = false;
      let accumulated = '';

      try {
        await chatAPI.streamMessage(content.trim(), sessionId, {
          onChunk: (text) => {
            accumulated += text;
            if (!assistantAdded) {
              assistantAdded = true;
              setIsTyping(false);
              setStreamingMessageId(assistantId);
              setMessages((prev) => [
                ...prev,
                {
                  id: assistantId,
                  role: 'assistant',
                  content: accumulated,
                  timestamp: new Date(),
                },
              ]);
            } else {
              setMessages((prev) =>
                prev.map((m) => (m.id === assistantId ? { ...m, content: accumulated } : m))
              );
            }
          },
          onDone: (data) => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId ? { ...m, provider: data.provider, truncated: data.truncated } : m
              )
            );
          },
        });
      } catch (err: unknown) {
        let errorMessage = 'Even TARS has his off days sometimes. Try that again?';

        if (err instanceof ChatStreamError) {
          if (err.status === 429) {
            errorMessage = "Whoa there, slow down! Too many messages. Give me a moment.";
          } else if (err.status === 500) {
            errorMessage = "Looks like I'm having technical difficulties. Try again in a moment?";
          } else if (err.message) {
            errorMessage = err.message;
          }
        } else {
          errorMessage = "Can't reach the server. Check your connection?";
        }

        setError(errorMessage);

        // Add error message to chat
        const errorMsg: Message = {
          id: `error-${Date.now()}`,
          role: 'system',
          content: errorMessage,
          timestamp: new Date(),
          isError: true,
        };
        setMessages((prev) => [...prev, errorMsg]);
      } finally {
        setIsTyping(false);
        setStreamingMessageId(null);
      }
    },
    [sessionId]
  );

  const clearMessages = useCallback(() => {
    const welcomeMsg = {
      ...WELCOME_MESSAGE,
      timestamp: new Date(),
    };
    setMessages([welcomeMsg]);
    localStorage.removeItem(MESSAGES_KEY);
  }, []);

  return {
    messages,
    isTyping,
    error,
    streamingMessageId,
    sendMessage,
    clearMessages,
  };
};
